import { useEffect, useState } from 'react';
import styles from './Login.module.css';
import * as Yup from 'yup';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { getUserByToken, linkDescopeToTP, login, MANAGER_PORTAL_URL } from '../../auth/_requests';
import { useAuth } from '../../auth/Auth';
import { Checkbox } from '../../helpers/UI/Checkbox/Checkbox';
import { motion } from 'framer-motion';
import { ErrorType } from '../../helpers/utils/types';
import axios from 'axios';
import logoUrl from '../../assets/helpers/navbar/tenantpay-white.png';
import OAuth from '../../helpers/OAuth/OAuth';
import { postLoginTask } from '../../helpers/utils/login';
import { useDescope } from '@descope/react-sdk';
import eyeOpen from '../../assets/pages/login/eye.svg';
import eyeOff from '../../assets/pages/login/eyeClosed.svg';
import { InlineAlert } from '../../helpers/UI/InlineAlert/InlineAlert';

import { logThirdPartyError } from '../../helpers/utils/safeSentryError';
import { setAnalyticsUser, trackLogin } from '../../helpers/utils/analytics';
import { AuthBrandedLoader } from './AuthBrandedLoader';
import { AuthPageShell } from './AuthPageShell';
import { ACCOUNT_DEACTIVATED_REASON, LOGOUT_REASON_KEY } from '../../configs/axios';
import { showToast } from '../../helpers/utils/showToast';

// Login page
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Password is required'),
});

const initialValues = {
  email: '',
  password: '',
};

export function Login() {
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const { auth, saveAuth, setCurrentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string>('');

  const descope = useDescope();

  const handleCheckBtn = () => {
    setIsChecked(!isChecked);
  };

  useEffect(() => {
    try {
      if (sessionStorage.getItem(LOGOUT_REASON_KEY) === ACCOUNT_DEACTIVATED_REASON) {
        sessionStorage.removeItem(LOGOUT_REASON_KEY);
        showToast({
          message: 'Your account has been deactivated. Please contact support@tenantpay.com.',
          type: 'error',
        });
      }
    } catch {
      // Storage can be unavailable; skip the notice.
    }
  }, []);

  useEffect(() => {
    // Check if user is already authenticated
    if (auth?.data.token) {
      if (auth) postLoginTask(auth, navigate, saveAuth);
      return;
    }
    // Fallback: check rememberMe cookie for backward compatibility
    const rememberMe = document.cookie.split('; ').find((row) => row.startsWith('rememberMe='));
    if (rememberMe && rememberMe.split('=')[1] === 'true') {
      navigate('/dashboard');
    }
  }, [navigate, auth]);

  /**
   * Sign in or sign up with email and password using Descope
   */
  const descopeEmailSignInOrUp = async (email: string, password: string) => {
    //There might be some old users which are present in the database but not in the descope
    //we need to signup the user if they don't exist in descope and sigin if they exist in descope
    try {
      const response = await descope.password.signUp(email, password);
      const errorDescription = response?.error?.errorDescription;
      if (errorDescription === 'User already exists') {
        try {
          await descope.password.signIn(email, password);
          return;
        } catch (signInError: any) {
          // Log error to Sentry
          const error = signInError instanceof Error ? signInError : new Error(String(signInError));
          logThirdPartyError(error, {
            method: 'descope.password.signIn',
            email,
            errorDescription: signInError?.error?.errorDescription,
            errorCode: signInError?.error?.errorCode,
          });
          // ONLY signin errors should block the flow
          throw signInError;
        }
      }
      return;
    } catch (signUpError: any) {
      const errorDescription = signUpError?.error?.errorDescription;

      // Log signup error to Sentry (but don't block login flow)
      const error = signUpError instanceof Error ? signUpError : new Error(String(signUpError));
      logThirdPartyError(error, {
        method: 'descope.password.signUp',
        email,
        errorDescription,
        errorCode: signUpError?.error?.errorCode,
      });
      //  Any other signup error should NOT stop login
      // Just log it and continue
      console.warn('Descope signup error (ignored):', errorDescription);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        const { data: auth } = await login(values.email, values.password);
        await descopeEmailSignInOrUp(values.email, values.password);
        saveAuth(auth);
        await linkDescopeToTP(auth.data.token);
        const { data: user } = await getUserByToken(auth.data.token);
        setCurrentUser(user);
        setAnalyticsUser(user.user?.userId, undefined, auth.data.tenantId);
        trackLogin('email');
        // Email verification MUST be first
        if (user.user?.isEmailVerified !== 1) {
          navigate('/verify-email');
          setSubmitting(false);
          setLoading(false);
          return;
        }

        if (isChecked) {
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + 30);
          document.cookie = `rememberMe=true; expires=${expirationDate.toUTCString()}; path=/`;
        }

        let loginContinued = true;

        await postLoginTask(auth, navigate, (newAuth) => {
          if (!newAuth) {
            loginContinued = false;
          }
          saveAuth(newAuth);
        });

        if (!loginContinued) {
          setSubmitting(false);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error(error);
        let errorMessage = 'The login details are incorrect';
        if (axios.isAxiosError(error)) {
          const responseMessage = (error.response?.data as ErrorType)?.message;
          switch (responseMessage) {
            case 'User does not exist':
              errorMessage = 'No account found. Sign up or verify your details.';
              break;
            case 'account_locked':
              errorMessage = 'Temporarily locked due to too many attempts. Try again in 5 mins.';
              break;
            case 'tenant_not_found':
              errorMessage =
                'This email is registered as a manager account. Redirecting you to the manager portal to sign in.';
              setTimeout(() => {
                window.location.href = MANAGER_PORTAL_URL;
              }, 2500);
              break;
            case 'user_is_inactive':
              errorMessage =
                'Your account has been locked. For assistance, please reach out to TenantPay support at support@tenantpay.com.';
              break;
            case 'phone_number_blocked':
              errorMessage =
                'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.';
              break;
          }
        }
        setServerError(errorMessage);
        saveAuth(undefined);
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  return (
    <>
      <AuthPageShell variant='center'>
        <div className={styles.loginCardShell}>
          <motion.main className={styles.main} initial={false}>
            <div className={styles.logoWrap}>
              <img src={logoUrl} className={styles.logo} alt='logo' decoding='async' />
            </div>

            {serverError && (
              <InlineAlert
                type='error'
                message={serverError}
                dismissible
                onDismiss={() => setServerError('')}
              />
            )}
            <div>
              {' '}
              <div className={styles.formTitle}>Welcome back.</div>
            </div>

            <div className={styles.oAuthOuter} onClick={(e) => e.preventDefault()}>
              <OAuth setLoading={setLoading} />
            </div>
            <div className={styles.authDivider}>
              <div className={styles.authDividerLine} />
              <span className={styles.authDividerText}>or continue with email</span>
              <div className={styles.authDividerLine} />
            </div>

            <form
              className={styles.form}
              onSubmit={formik.handleSubmit}
              noValidate
              id='kt_login_signin_form'
            >
              <div className={styles.formUpper}>
                <div className={styles.formBoxes}>
                  <div className={styles.formBoxesInner}>
                    <div className={'inputWrapper'} title='Enter your registered email to login'>
                      <div className={'label'}>Email</div>

                      <div
                        className={clsx(
                          formik.touched.email && formik.errors.email ? styles.isInvalid : '',
                          formik.touched.email && !formik.errors.email ? styles.valid : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            formik.handleSubmit();
                          }
                        }}
                      >
                        <input
                          {...formik.getFieldProps('email')}
                          placeholder='you@email.com'
                          type='email'
                          name='email'
                          autoComplete='email'
                        />
                      </div>
                      {formik.touched.email && formik.errors.email && (
                        <div className={styles.emailError}>
                          <span>{formik.errors.email}</span>
                        </div>
                      )}
                    </div>
                    <div
                      className={'inputWrapper'}
                      title='Enter your password to access your account'
                    >
                      <div className={'label'}>Password</div>
                      <div
                        className={clsx(
                          formik.touched.password && formik.errors.password ? styles.isInvalid : '',
                          formik.touched.password && !formik.errors.password ? styles.valid : '',
                          'inputField'
                        )}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      >
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={'••••••••'}
                          value={formik.values.password}
                          onChange={(e) => {
                            formik.setFieldValue('password', e.target.value);
                          }}
                          autoComplete='current-password'
                          className={styles.passwordInputBox}
                          onBlur={formik.handleBlur('password')}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              formik.handleSubmit();
                            }
                          }}
                        />

                        <button
                          type='button'
                          onClick={(event) => {
                            event.preventDefault();
                            setShowPassword(!showPassword);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                            }
                          }}
                        >
                          <img
                            src={showPassword ? eyeOpen : eyeOff}
                            alt='eye-img'
                            className={styles.eyeImg}
                          />
                        </button>
                      </div>
                      {formik.touched.password && formik.errors.password && (
                        <div className={styles.emailError}>
                          <div className='fv-help-block'>
                            <span>{formik.errors.password}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.forgotBox}>
                    <div className={styles.rememberOuter}>
                      <Checkbox
                        variant='auth'
                        checked={isChecked}
                        onChange={() => {
                          handleCheckBtn();
                        }}
                      />
                      <div className={styles.remember}>Remember me</div>
                    </div>
                    <button
                      type='button'
                      onClick={(e) => {
                        navigate('/forgot-password');
                        e.preventDefault();
                      }}
                      className={styles.forgotPassword}
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
              </div>

              <button
                type='submit'
                id='kt_sign_in_submit'
                disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                className={`${styles.submitBtn} ${
                  (formik.isSubmitting || !formik.isValid || !formik.dirty) && styles.disableBtn
                }`}
              >
                {!loading && <span className='indicator-label'>Sign in</span>}
                {loading && (
                  <span className={styles.indicatorProgress} style={{ display: 'block' }}>
                    Please wait...
                    <span className={styles.spinner}></span>
                  </span>
                )}
              </button>
            </form>
            <div className={styles.signInBox}>
              <span>Your first time?</span>{' '}
              <button
                type='button'
                onClick={() => {
                  logout();
                  navigate('/sign-up');
                }}
                className={styles.forgotPassword}
              >
                Become a TenantPay member
              </button>
            </div>
          </motion.main>
        </div>
      </AuthPageShell>
      {loading && <AuthBrandedLoader message='Signing in…' />}
    </>
  );
}
